import '@babylonjs/core/Engines/Extensions/engine.dynamicTexture';
import '@babylonjs/core/Materials/standardMaterial';

import { HubConnectionState } from '@microsoft/signalr';

import { AssistantController } from '../assistant/AssistantController';
import { MicIndicatorRenderer } from '../assistant/MicIndicatorRenderer';
import { MicrophoneCapture } from '../assistant/MicrophoneCapture';
import { NodeLabelController } from '../assistant/NodeLabelController';
import { AuthoringController } from '../authoring/AuthoringController';
import { AvatarManager } from '../avatars/AvatarManager';
import { DebugOverlay } from '../core/DebugOverlay';
import { DesktopControls } from '../core/DesktopControls';
import { SceneRoot } from '../core/SceneRoot';
import { XrManager } from '../core/XrManager';
import { DevSim } from '../dev/DevSim';
import type { AuthUserDto, RoomDto, RoomTemplateDto } from '../generated/contracts';
import { DebugPanel } from '../input/DebugPanel';
import { ExitSign } from '../input/ExitSign';
import { GrabberHub } from '../input/Grabbers';
import { handedness } from '../input/Handedness';
import { LobbyInput } from '../input/LobbyInput';
import { LobbyReturnSign } from '../input/LobbyReturnSign';
import {
  getDiagramSummary,
  getLobbyDiagrams,
  getNodeTypes,
  getRoomCapacity,
  getRoomSnapshot,
} from '../net/api';
import { quatToArray, vec3ToArray } from '../net/packing';
import { RoomHub } from '../net/roomHub';
import { TransformStreamer } from '../net/TransformStreamer';
import type { PoseDto } from '../net/wire';
import { ErrorLogPanel } from '../room/ErrorLogPanel';
import { LobbyView } from '../room/LobbyView';
import { ModelLibrary } from '../room/ModelLibrary';
import { RoomState } from '../room/RoomState';
import { RoomView } from '../room/RoomView';
import { SessionScreen } from '../ui/SessionScreen';
import { Splash, type EntryMode } from '../ui/Splash';
import { PeerVoicePlayer } from '../voice/PeerVoicePlayer';

/**
 * Everything the room session used to read from `main.ts`'s module scope, now stated.
 *
 * The four DOM hosts stay nullable rather than being narrowed by the caller, because the check
 * that they exist — and the error message naming the four missing element ids — is part of this
 * module's own contract and has been since before the move. The three optional ones are guarded
 * individually at each use, also unchanged.
 */
export interface RoomRouteDeps {
  /** `#render`. Required — see the check at the top of {@link mountRoomRoute}. */
  canvas: HTMLCanvasElement | null;
  /** `#status`. Required. */
  statusHost: HTMLElement | null;
  /** `#splash`. Required. */
  splashHost: HTMLElement | null;
  /** `#session`. Required. */
  sessionHost: HTMLElement | null;

  /** `#hint`. Optional page chrome; every use is guarded. */
  hint: HTMLElement | null;
  /** `#reenter`. Optional. */
  reenterButton: HTMLButtonElement | null;
  /** `#leave-room`. Optional. */
  leaveRoomButton: HTMLButtonElement | null;

  /**
   * The signed-in account. Nullable, and checked here rather than by the caller, so the
   * "reached with no signed-in account" error survives the move intact — it is a statement about
   * the router's guard having run, which this module is the one that notices.
   */
  account: AuthUserDto | null;

  /** The dev-only `window.tunet` handle this session hangs its objects off in development. */
  devHandle: Record<string, unknown>;

  /**
   * Router navigation with a rejection handler already attached — `main.ts`'s `safeNavigate`.
   * Passed as a function rather than the `Router` itself so this module cannot navigate in any
   * way the shell has not sanctioned, and so a test can drive it without one.
   */
  navigate: (path: string) => void;

  /** The transient toast over the canvas. */
  showNotice: (message: string) => void;

  /** Replaces the splash with the fatal-error screen. */
  showFatalError: (error: unknown) => void;
}

/**
 * Whether this session belongs to the owner's own seeded account — the gate on `DebugPanel` and
 * `ErrorLogPanel`, and on nothing else.
 *
 * **A convenience, not a security boundary**, exactly as it was before: both panels are
 * diagnostic scaffolding, and everything they can do a signed-in account could already do
 * through the hub. `DebugPanel.ts`'s own doc comment carries the full argument.
 *
 * **It reads the display name because the address stopped being a stable key for this
 * question.** The check used to be `currentAccount.username === 'lars'`, and `DbSeeder` rewrites
 * that username from `SeedUsers.First` on every start, so it held in every environment. The
 * address does not: a seeded account that has adopted a real Google identity carries whatever
 * address that identity reported, which is not the one in the seed, so keying on the address
 * would have silently switched both panels off in production. `DisplayName` is the field
 * `DbSeeder` still rewrites unconditionally on every start, so it is exactly as reliable a key
 * as the username was — and the failure mode if an invited person is also called "Lars" is that
 * they see a diagnostics panel, which is the class of thing this gate was already declared not
 * to be protecting against. The genuinely right key is the account id, and it is not on
 * `AuthUserDto`; putting it there is a contract change worth making the day this gate is worth
 * more than it is.
 */
function isOwnersOwnAccount(account: AuthUserDto): boolean {
  return account.displayName === 'Lars';
}

/**
 * The room session: scene, hub connection, XR, authoring, assistant, voice — everything that
 * happens once somebody is actually standing in a room.
 *
 * **This is `main.ts`'s former body, moved rather than rewritten.** It was 1,279 of that file's
 * 1,706 lines and touched by 30 of the last 200 commits, which made it the one place every room
 * feature had to be added and therefore the one place two changes could not be made at once.
 * Nothing inside it changed in the move: the same objects are built in the same order, wired the
 * same way, and torn down by the same disposer. What changed is the boundary — the fifteen things
 * it used to read straight out of module scope are now {@link RoomRouteDeps}, stated once at the
 * top instead of reached for from anywhere in 1,200 lines.
 *
 * **Why that boundary is the point, and not merely tidier.** Two things follow from it that could
 * not be done before:
 *
 *   * The room stack stops being part of the application's entry graph. `main.ts` no longer
 *     imports Babylon, WebXR, the authoring stack or the hub at module scope, so a visitor who
 *     only ever opens the portal or a shared diagram link stops paying for them.
 *   * This module is what becomes the VR client's own entry when it moves to its own bundle and
 *     its own host (plan: `vr.tunet.dev`). A module with an explicit dependency object moves;
 *     a closure over another file's module scope does not.
 *
 * **What is deliberately still wrong here.** The body is one 1,200-line function. Breaking it
 * into its own hand-labelled sections — session, scene, room, signage, hub, authoring, xr, entry,
 * which are already marked in it as comments — is real work with real risk and is deliberately a
 * separate step: doing it in the same change as the move would make the diff unreadable and
 * remove the one property that makes this change safe, which is that nothing inside it moved.
 */
export async function mountRoomRoute(deps: RoomRouteDeps): Promise<() => void> {
  const {
    canvas,
    statusHost,
    splashHost,
    sessionHost,
    hint,
    reenterButton,
    leaveRoomButton,
    devHandle,
    account,
    navigate,
    showNotice,
    showFatalError,
  } = deps;

  if (!canvas || !statusHost || !splashHost || !sessionHost) {
    throw new Error('Page markup is missing #render, #status, #splash or #session.');
  }

  if (account === null) {
    throw new Error('mountRoomRoute reached with no signed-in account — the guard should have prevented this.');
  }

  // Captured once, as its own const: `account` is a module-level `let` the router's guard
  // can reassign between navigations, and this function's body below has plenty of nested
  // closures (hub.on handlers, button callbacks) between here and its later reads — a bare
  // `account` at any of those points would lose TypeScript's null-narrowing across the
  // closure boundary even though nothing in this function ever reassigns it itself.
  const currentAccount = account;

  // --- session ----------------------------------------------------------------------

  const splash = new Splash(splashHost, currentAccount.displayName);
  splash.onPortalRequested = () => navigate('/portal');

  const sessionScreen = new SessionScreen(sessionHost);

  Object.assign(devHandle, { splash, sessionScreen });

  splash.setCheck('backend', 'connecting to the backend', 'pending');
  splash.setCheck('room', 'loading the room', 'pending');
  splash.setCheck('headset', 'looking for a headset', 'pending');

  // THE SPLASH NO LONGER PICKS A ROOM, AND THERE IS NO ROOM LIST TO PREFETCH.
  //
  // This used to fetch GET /api/rooms so the entry screen could show the org's rooms and let
  // somebody choose one. SPEC-153 abolished that outright: a room is spawned by entering VR from
  // a diagram, and nobody creates, names or picks one. A picker listing whatever ephemeral rooms
  // happen to exist right now would be offering to drop the caller into other people's live
  // sessions, which is the opposite of what the list meant when rooms were permanent.
  //
  // What replaces it is the lobby (SPEC-156) — diagrams around you as live miniatures — and
  // until that lands, the portal's diagram list. Either way the thing being chosen is a
  // diagram; the room follows from it.

  // Resolved from the entry gesture (`splash.onMicRequested`, below) or, when the browser
  // already holds a granted permission, speculatively before that — never from a standalone
  // button any more (Splash.ts's own doc on `onMicRequested`: the owner asked for it gone once
  // auto-acquire made it "not functional," and the entry press is what replaces it as the one
  // remaining user gesture that can still raise a first-time prompt — a prompt cannot render
  // inside an XR session, the same "anything slow goes before the choice" rule CLAUDE.md
  // already states for the rest of boot). Declining is not an error: read once, below, after
  // the room is known, and folded into assistant.configure() the same way "no assistant
  // configured" is — push-to-talk stays off, everything else works normally.
  let micPermissionGranted = false;

  // Memoised rather than a fresh call every time: the auto-acquire check below and the entry
  // gesture can both want to trigger this, and a real `getUserMedia` call must only ever happen
  // once — a second call while the first is still outstanding would be redundant at best and,
  // for a not-yet-decided permission, could throw a second prompt at a moment with no fresh user
  // gesture behind it. Every caller gets the same promise and the same eventual outcome.
  let micPermissionRequest: Promise<void> | null = null;

  const requestMicrophonePermission = (): Promise<void> => {
    if (micPermissionRequest) {
      return micPermissionRequest;
    }

    splash.setMicrophoneState('pending', 'microphone — requesting…');

    micPermissionRequest = MicrophoneCapture.requestPermission().then((granted) => {
      micPermissionGranted = granted;
      splash.setMicrophoneState(
        granted ? 'ready' : 'unavailable',
        granted ? 'microphone — enabled' : 'microphone — permission declined',
      );
    });

    return micPermissionRequest;
  };

  splash.setCheck('microphone', 'microphone — requested on entry', 'pending');

  // Awaited by Splash.submit() before the entry choice resolves (Splash.ts's own doc on
  // `onMicRequested`) — the actual `getUserMedia` call, and the one place left that can raise a
  // first-time permission prompt from a real user gesture, now that there is no standalone
  // button of its own to raise it from.
  splash.onMicRequested = () => requestMicrophonePermission();

  // Auto-acquire ahead of the entry press, without waiting for one, whenever the browser
  // already holds a granted permission — the owner's original report was having to press
  // "Enable microphone" every session even though the browser had already decided this.
  // `MicrophoneCapture.requestPermission()`'s own `getUserMedia` call resolves such a grant
  // instantly, with no prompt, so calling it here costs nothing when it turns out not to apply,
  // and `requestMicrophonePermission`'s own memoisation means the entry press later just
  // reuses whatever this already started (or already finished).
  //
  // `navigator.permissions.query({ name: 'microphone' })` is not implemented for microphone in
  // every browser (jsdom has no `permissions` at all; this is also, concretely, Quest
  // Browser's own support that's in question), so this whole block is defensive: an absent
  // `permissions` object, a query that throws synchronously, or a rejected promise all fall
  // back to the entry-press path untouched — `onMicRequested` above is still wired and still the
  // only way in for those cases. Deliberately only acts on `'granted'`: calling `getUserMedia`
  // speculatively for `'prompt'` (or any other/unknown state) would either fail outright or
  // throw a permission dialog at a moment with no user gesture behind it, exactly what folding
  // the request into the entry press exists to avoid.
  try {
    navigator.permissions
      ?.query({ name: 'microphone' })
      .then((status) => {
        if (status.state === 'granted') {
          void requestMicrophonePermission();
        }
      })
      .catch(() => {
        // Query rejected — most likely 'microphone' isn't a recognised permission name on
        // this browser. Leave the entry-press path as the only way in.
      });
  } catch {
    // Some browsers throw synchronously for an unsupported query() call rather than
    // rejecting a promise. Same fallback: the entry press still works.
  }

  const choicePromise = splash.waitForChoice();

  // --- scene ------------------------------------------------------------------------

  const sceneRoot = new SceneRoot(canvas);
  const overlay = new DebugOverlay(sceneRoot.scene, statusHost);
  const desktop = new DesktopControls(sceneRoot.scene, canvas);

  sceneRoot.scene.activeCamera = desktop.camera;
  sceneRoot.start();

  // One ModelLibrary, shared by the room's own nodes and the lobby's miniatures (SPEC-191).
  // `DiagramMiniature` used to default to a fresh, empty library of its own — preloaded by
  // nobody, ever — so a model-backed node in any diagram rendered as its procedural silhouette
  // in the lobby regardless of how it looked in the room itself; six catalogue types share the
  // plain box (CLAUDE.md), which is what a headset report of "a bunch of boxes" turned out to be.
  // Passed to both `view` and `lobby` below; `view.preloadModels(nodeTypes)`, further down, is
  // still the only place anything is ever actually fetched into it.
  const models = new ModelLibrary(sceneRoot.scene);

  const view = new RoomView(sceneRoot.scene, sceneRoot.roomOrigin, models);
  const state = new RoomState(view);
  const avatars = new AvatarManager(sceneRoot.scene);

  // THE LOBBY IS PART OF THE ROOM, NOT A SCREEN BEFORE IT (SPEC-161). A person is always in a
  // room; the lobby is that room while it shows no diagram, and opening one puts it in the middle
  // of where they already are. So this is built here, in the room scene, rather than being a
  // separate route with its own boot — which is also what makes opening a diagram cost nothing
  // but a state swap.
  const lobby = new LobbyView(sceneRoot.scene, sceneRoot.roomOrigin, getDiagramSummary, models);

  // Opening a diagram is a hub call, not a navigation: the room stays, the party stays, and the
  // diagram appears in the middle of where everyone already is (SPEC-161, SPEC-064). The
  // broadcast that follows is what updates this client too, through the same `diagramShown`
  // handler everybody else in the room goes through — so the person who opened it and the people
  // who did not converge on identical state by identical means.
  // Named rather than left inline because it is the one place "open this diagram" happens, and
  // it is worth being able to point at. Reaching out in the lobby and picking one is its only
  // caller: arriving on a link that names a diagram does NOT come through here, because
  // `POST /api/diagrams/{id}/room` hands back the room already showing it — such a client is
  // looking at the diagram before this module ever runs.
  const openDiagram = async (diagramId: string): Promise<void> => {
    const shown = await hub.showDiagram(diagramId);

    state.applyDiagramShown(shown.snapshot, shown.activeLocks);
    view.setRoomFit(shown.snapshot.template.floorplan);
    showLobbyIfIdle(shown.snapshot.room, shown.snapshot.template);
  };

  // The other direction (SPEC-199): closing whatever diagram the room is showing and returning
  // it to the lobby. `ShowDiagram` already takes a nullable diagram id and always has —
  // `openDiagram`'s own counterpart needed no new hub method, no wire.ts edit and no codegen
  // (plan/lobby-v1.md's own correction on this point) — just this, the same five lines with a
  // null argument, reusing the identical `applyDiagramShown`/`setRoomFit`/`showLobbyIfIdle` path.
  // `LobbyReturnSign.onReturnRequested` (wired further down, once `authoring` exists) is this
  // function's only caller.
  // spec: SPEC-199
  const closeDiagram = async (): Promise<void> => {
    const shown = await hub.showDiagram(null);

    state.applyDiagramShown(shown.snapshot, shown.activeLocks);
    view.setRoomFit(shown.snapshot.template.floorplan);
    showLobbyIfIdle(shown.snapshot.room, shown.snapshot.template);
  };

  const lobbyInput = new LobbyInput(lobby, (diagramId) => void openDiagram(diagramId));

  Object.assign(devHandle, { sceneRoot, view, state, overlay, desktop, avatars, lobby });

  // Preloaded now, before the first peer ever joins, for the same reason the node catalogue's
  // own models are preloaded before the first node is built: Avatar's construction is
  // synchronous (AvatarManager.preloadModels). No avatar .glb exists yet, so this currently
  // resolves having loaded nothing and every avatar keeps rendering its built-in primitives.
  await avatars.preloadModels();

  // --- room -------------------------------------------------------------------------

  // Which room to join (plan/identity-and-portal.md; extended by the room-picker task).
  // PortalHome's room links, and now Splash's own room picker (`splash.onRoomSelected`
  // above), both navigate to `/room?roomId=<id>` — Router.ts matches routes by pathname and
  // carries the query string along untouched, so this reaches here unchanged either way (see
  // Router.ts's own doc comment on `pathnameOf` for the bug that once broke exactly this).
  // No explicit choice (a direct "/" landing on this route, or a bookmark to bare "/room")
  // falls back to GET /api/bootstrap, the caller's own default room.
  const requestedRoomId = new URLSearchParams(window.location.search).get('roomId');

  if (requestedRoomId === null) {
    // NO ROOM ID, AND NO LONGER ANY SUCH THING AS "YOUR DEFAULT ROOM" TO FALL BACK TO.
    //
    // This used to resolve one through GET /api/bootstrap, which answers "the first room of your
    // team by name". That was a coherent answer while every org had one permanent room. It stopped
    // being one when rooms became ephemeral (SPEC-153: nobody creates, names or picks a room):
    // with no live room the call answers 503 and this screen stranded — Enter VR and Desktop mode
    // permanently disabled, and a message advising a sign-out that could not possibly help, since
    // no amount of signing in creates a room. With a live room it was arguably worse, silently
    // dropping the caller into whatever session somebody else in their org had left open.
    //
    // So this navigates to the portal to choose a diagram, and Enter VR spawns a room for it.
    // INTERIM: the lobby (SPEC-156) is the intended arrival — diagrams standing around you as
    // live miniatures, opening one being how a session begins — and replaces this outright.
    //
    // `navigate`, not a bare `router.navigate`, which is what this used to be: the wrapper is
    // what attaches a rejection handler, and this call had none. It is also the tail of a mount
    // function whose contract is to return a teardown, and the navigation it starts tears this
    // very route down — so it is deliberately not awaited either way.
    navigate('/portal');

    return () => {
      // Same teardown as the route's own cleanup further down, minus xr (never built this
      // early) — view/avatars/overlay/desktop all still need the live scene, so before
      // sceneRoot.dispose(), not after.
      view.dispose();
      lobby.dispose();
      avatars.dispose();
      overlay.dispose();
      desktop.dispose();

      sceneRoot.dispose();
      splash.dispose();
      sessionScreen.dispose();
    };
  }

  const roomId: string = requestedRoomId;

  // The catalogue before the snapshot: nodes cannot be built until their types are known, and
  // the entry screen is where anything slow belongs. Preloading models is the same rule applied
  // to model-backed types specifically: node creation is synchronous, so this has to finish
  // before the first node is built rather than on first placement (CLAUDE.md, "The splash
  // screen exists so entering VR is instant").
  const [nodeTypes, roomCapacity] = await Promise.all([getNodeTypes(), getRoomCapacity()]);
  view.setCatalog(nodeTypes);
  await view.preloadModels(nodeTypes);

  // "The model failed to load" and "this type was never model-backed" are indistinguishable
  // from a deployed bundle otherwise (ModelLibrary.ts, EXPECTED_MODEL_KEYS) — reported once,
  // right after preload settles, since the outcome is fixed for the rest of this session.
  sessionScreen.setModelStatus(view.modelSummary());

  const snapshot = await getRoomSnapshot(roomId);

  // Built from the REST snapshot so the geometry exists before XR is set up —
  // teleportation needs real floor meshes to aim at.
  sceneRoot.buildRoom(snapshot.room, snapshot.template);

  // Which shell `sceneRoot` currently has built, tracked separately from the lobby's own
  // idle flag — see `showLobbyIfIdle`'s own doc comment (further down) for why this exists and
  // why `null` (tracked, idle) is distinct from `undefined` (not tracked yet). Set here, right
  // alongside the `buildRoom` call above that actually builds the shell this describes, so the
  // two can never start disagreeing about which pointer the room was last built from.
  let shellDiagramId: string | null = snapshot.room.diagramId ?? null;

  // Canonical -> this room's own floor (SPEC-131, RoomView.setRoomFit) — told before
  // `state.reset` runs (further down, after `hub.start()`), which is what actually builds the
  // first meshes from it; setting it here, right alongside `buildRoom`, keeps every
  // floorplan-derived setup in this section reading from the same one fetch.
  view.setRoomFit(snapshot.template.floorplan);

  const { width, depth } = snapshot.template.floorplan;

  // The desktop boundary clamp (plan/session-exit-and-membership-fixes.md, A3) — told the
  // room's own extents right alongside every other floorplan-derived setup below, and again on
  // every `buildRoom()` rebuild (`hub.onResynced`, further down): the room is not a constant,
  // and neither is the boundary a desktop visitor should be kept inside.
  desktop.setRoomExtents(width, depth);

  splash.setCheck(
    'room',
    `${snapshot.room.name} · ${width}×${depth}m · ${snapshot.nodes.length} nodes`,
    'ready',
  );

  if (snapshot.nodes.length === 0) {
    splash.setCheck('empty', 'room is empty — seed it with: npm run demo', 'unavailable');
  }

  // --- signage ------------------------------------------------------------------------

  // The room name itself is painted directly onto the north wall's own top beam by
  // `sceneRoot.buildRoom()` (`RoomPlaque.ts`'s own module comment) — `room.name` is already one
  // of `buildRoom`'s own parameters, so there is no separate object to construct or reposition
  // here any more, unlike the free-standing plaque this replaced. It survives a resync the same
  // way every other room-shell surface does: `hub.onResynced` below always calls
  // `sceneRoot.buildRoom(result.snapshot.room, result.snapshot.template)` again in full.

  // The admin-only error/notice log (plan/in-room-signage.md), south wall, left of DebugPanel.
  // Visibility matches DebugPanel exactly — the identical `isOwnersOwnAccount` check, not a
  // second, differently-scoped gate (see ErrorLogPanel.ts's own module comment for why).
  // Constructed here, ahead of `assistant`/`nodeLabel` just below, because their own `onNotice`
  // wiring feeds it through `notify`.
  const errorLog = new ErrorLogPanel(sceneRoot.scene, isOwnersOwnAccount(currentAccount));
  errorLog.reposition(snapshot.template.floorplan);

  /** Fans every notice out to both the 2D `#notice` element (still useful for a desktop
   *  spectator — plan/in-room-signage.md, "The 2D #notice element stays as it is") and the
   *  wall log. A no-op into `errorLog` for anyone but `lars` — see `ErrorLogPanel.record()`'s
   *  own gate. */
  const notify = (message: string): void => {
    showNotice(message);
    errorLog.record(message);
  };

  /**
   * `window.onerror` and `unhandledrejection` — plan/in-room-signage.md: "These matter most:
   * nothing reports them today, and 'something is weird' is exactly the unreported case."
   * Wall-log only, not `notify`/the 2D element: these are not one of `showNotice`'s existing
   * four producers, and a desktop session already has its own devtools console for them.
   * Removed in this route's own cleanup, further down, matching the brief's own "make sure you
   * remove these listeners on teardown."
   */
  const handleWindowError = (event: ErrorEvent): void => {
    errorLog.record(event.message || 'window error');
  };
  const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const { reason } = event;
    errorLog.record(`unhandled rejection: ${reason instanceof Error ? reason.message : String(reason)}`);
  };

  window.addEventListener('error', handleWindowError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  // --- hub --------------------------------------------------------------------------

  const hub = new RoomHub();
  const streamer = new TransformStreamer(hub);

  // One hot recorder, shared by every consumer that needs a live microphone below — `assistant`
  // (Y, or an open push-to-talk turn) and `nodeLabel` (a held dominant-hand button while
  // holding a shape) each subscribe to and unsubscribe from this same instance independently
  // rather than opening the device twice (`MicrophoneCapture`'s own module doc, "one hot
  // recorder, N consumers"). Constructed once, here, and passed to both.
  const microphoneCapture = new MicrophoneCapture();

  // Constructed here, alongside the hub it wraps — its own three hub.on() registrations
  // (assistantTurnStarted/assistantReply/assistantTurnFailed) need a connection to listen
  // on, not a joined room, so this can happen well before hub.join() below. The
  // MicIndicatorRenderer half of its wiring waits for `grabbers` to exist, in the
  // authoring section — it polls sceneRoot.assistantGlow itself rather than a second callback,
  // so onGlowChanged below stays a single listener (see MicIndicatorRenderer's own module doc).
  // configure() — which needs both the microphone permission outcome and the room's own
  // assistantAvailable flag — waits for join() to resolve, further down still.
  const assistant = new AssistantController(hub, microphoneCapture);
  assistant.onNotice = notify;

  // assistant.onGlowChanged is wired further down, once `debugPanel` exists — see the comment
  // there for why the two are wired together.

  // The 'speaking' light's own amplitude signal — see AssistantController's own
  // PlaybackAmplitude analyser over the reply clip's actual playback. A plain passthrough,
  // the same shape as onGlowChanged just above: this client's own light is driven by this
  // client's own playback, which is correct with no synchronisation, since every client
  // receives the same clip at roughly the same moment and plays it (and therefore analyses
  // it) independently.
  assistant.onAmplitudeChanged = (level) => sceneRoot.setAssistantAmplitude(level);

  // Player-to-player voice (plan/ai-in-room.md): everyone else's live microphone, not
  // only the assistant's replies. Constructed here for the same reason `assistant` is —
  // its hub.on() registrations (wired just below) need a connection to listen on, not a
  // joined room. `hub.peerId` reads as null until join() resolves further down, which is
  // exactly right: no voiceStreamStarted/voiceChunk/voiceStreamEnded can arrive before
  // then either.
  const voice = new PeerVoicePlayer(() => hub.peerId);
  // Not one of plan/in-room-signage.md's own named four producers, but the identical "invisible
  // in a headset" gap applies to it too — a codec refusal is exactly the kind of thing "if
  // something is weird" ought to have somewhere to look for.
  voice.onUnsupported = (mimeType) =>
    notify(`can't play back other people's voices here — unsupported format (${mimeType})`);

  // Speaking a shape's label while holding it (plan/open-items.md) — a logically separate
  // capture from `assistant`'s (labelling never routes through a conversational turn), but the
  // *same* `microphoneCapture` instance: sharing it, rather than opening a second independent
  // stream off the same hardware, is what lets this subscribe successfully even when the
  // room's own microphone is already on — see `NodeLabelController`'s own top-level doc for the
  // headset report this fixed. Constructed here, alongside the hub and the assistant, for the
  // same reason: its one hub.on() registration needs a connection to listen on, not a joined
  // room yet.
  const nodeLabel = new NodeLabelController(hub, microphoneCapture);
  nodeLabel.onNotice = notify;

  // The "listening…"/"transcribing…"/"didn't catch that" status NodeLabelController puts on
  // the node being renamed (plan/open-items.md, "STT on node renaming... without any
  // feedback") — a plain passthrough to `view`, which already exists by this point. Not
  // `hub.on('nodeUpdated', ...)` here as a second registration for that event — see
  // `NodeLabelController`'s own constructor remarks for why that would clobber the one below.
  nodeLabel.onLabelStatusChanged = (nodeId, text) => view.setLabelStatus(nodeId, text);

  Object.assign(devHandle, { hub, streamer, assistant, nodeLabel, voice });

  const refreshCounts = () =>
    overlay.set({
      peers: state.peers.size,
      nodes: state.nodes.size,
      connections: state.connections.size,
    });

  hub.onConnectionStateChanged = (connectionState) => {
    overlay.set({
      connection:
        connectionState === HubConnectionState.Connected
          ? 'connected'
          : connectionState === HubConnectionState.Reconnecting
            ? 'reconnecting…'
            : connectionState.toLowerCase(),
    });

    // Hub connection errors (plan/in-room-signage.md, "What feeds it") — the one clean existing
    // seam for them. `Connecting`/`Disconnecting`/`Connected` are ordinary lifecycle, not
    // something worth a wall-log entry (every session passes through `Connecting` ->
    // `Connected` once at boot); `Reconnecting`/`Disconnected` are the genuinely noteworthy
    // drops "if something is weird" is asking about.
    if (
      connectionState === HubConnectionState.Reconnecting ||
      connectionState === HubConnectionState.Disconnected
    ) {
      errorLog.record(`hub connection: ${connectionState.toLowerCase()}`);
    }
  };

  // Durable changes go through state, which decides what is true and tells the view.
  hub.on('nodeCreated', (node) => {
    state.upsertNode(node);
    refreshCounts();
  });
  hub.on('nodeUpdated', (node) => {
    state.upsertNode(node);

    // A successful voice-labelling capture reaches this connection exactly this way — a plain
    // NodeUpdated, the same as any other edit — so this is the one place it can be recognised.
    // See NodeLabelController's own constructor remarks for why this is a plain method call
    // rather than a second `hub.on('nodeUpdated', ...)` registration.
    nodeLabel.handleNodeUpdated(node);
  });
  hub.on('nodeDeleted', (nodeId) => {
    state.removeNode(nodeId);
    refreshCounts();
  });
  hub.on('connectionCreated', (connection) => {
    state.upsertConnection(connection);
    refreshCounts();
  });
  hub.on('connectionUpdated', (connection) => state.upsertConnection(connection));
  hub.on('connectionDeleted', (connectionId) => {
    state.removeConnection(connectionId);
    refreshCounts();
  });

  // StrokeCreated is OthersInGroup-only — this client's own committed strokes arrive via
  // CommitStroke's return value instead (DrawingManager.commit), never here. StrokeDeleted
  // goes to the whole Group, including whoever asked for the delete, so this also fires
  // (redundantly, harmlessly) for this client's own erases.
  hub.on('strokeCreated', (stroke) => state.upsertStroke(stroke));
  hub.on('strokeDeleted', (strokeId) => state.removeStroke(strokeId));

  hub.on('nodeLockAcquired', (nodeId, byPeerId) => state.applyLockAcquired(nodeId, byPeerId));
  hub.on('nodeLockReleased', (nodeId, finalTransform, newVersion) =>
    state.applyLockReleased(nodeId, finalTransform, newVersion),
  );

  // The room's accent colour changed — today, only ever via the assistant's
  // set_room_accent_color tool (plan/ai-in-room.md, "The tool registry"), but this is a
  // plain hub method any future UI could call too. RoomUpdated is only ever broadcast from
  // UpdateRoomAccent, which touches nothing on the Room but accentColor, so this goes through
  // sceneRoot.updateRoomAccent — a live retint against the room's existing materials — rather
  // than buildRoom(). A headset report ("the whole top part of the walls flicker through black
  // when the colour fade starts") turned out to be buildRoom() itself: rebuilding live room
  // geometry on every accent change meant every material in the room, not just the accent one,
  // recompiled its shader, and a real WebGL engine draws a not-ready mesh as the scene's own
  // near-black clear colour until that finishes — see updateRoomAccent's own doc comment in
  // SceneRoot.ts for the full mechanism.
  hub.on('roomUpdated', (room) => sceneRoot.updateRoomAccent(room.accentColor));

  hub.on('peerJoined', (peer) => {
    state.addPeer(peer);
    avatars.add(peer);
    refreshCounts();
  });
  hub.on('peerLeft', (peerId) => {
    state.removePeer(peerId);

    // avatars.remove() disposes the peer's Avatar, which (Avatar.dispose) already tears down
    // its own VoiceBands/VisorVisualizer regardless of whether a matching voiceStreamEnded ever
    // arrives — the same disconnect-mid-stream gap voice.end() below exists for on the
    // PeerVoicePlayer side. Removing the avatar first, then ending the voice stream, mirrors
    // that order.
    avatars.remove(peerId);

    // The hub keeps no per-peer voice state, so there is no VoiceStreamEnded for a peer
    // who disconnects mid-stream (IRoomHubClient.VoiceStreamEnded's own remarks) — this is
    // the disconnect cleanup path instead. voice.remove(), not voice.end(): the peer is
    // gone, not just quiet, so this wants the hard teardown rather than end()'s "drain
    // whatever is still queued" path — see PeerVoicePlayer.remove's own doc for why end()
    // alone can never be reached at all if audio.play() was ever refused by the browser's
    // autoplay policy (start()'s own catch). Harmless if that peer had no stream open:
    // remove() is a no-op for a peerId it is not tracking, the same tolerance end() has.
    voice.remove(peerId);

    refreshCounts();
  });

  // A peer's dominant hand now shows whichever prop their mode holds (plan/remote-tool-
  // visibility.md) — one code path for an ordinary broadcast and for JoinRoom's own replay to
  // this connection alone; AvatarManager.applyToolSwitch is what makes an event naming a peer
  // this client does not know about yet harmless rather than an error (see that method's own
  // remarks), so this registration needs no ordering guard against peerJoined/join() itself.
  hub.on('toolSwitched', (peerId, mode, hand) => avatars.applyToolSwitch(peerId, mode, hand));

  // Streamed poses bypass state entirely: they are never persisted, and the durable write
  // for a moved node happens once, when its holder releases the lock.
  hub.on('nodeTransform', (update) => view.applyLiveTransform(update));
  hub.on('avatarPose', (update) => avatars.apply(update));

  // Player-to-player voice (plan/ai-in-room.md): never written to state or persisted, the
  // same rule every other streamed message on this hub follows — this only ever drives
  // PeerVoicePlayer's own live MediaSource/SourceBuffer pipeline, one per speaking peer.
  hub.on('voiceStreamStarted', (peerId, mimeType) => {
    voice.start(peerId, mimeType);

    // The visor voice visualisation (AvatarManager.ts, VisorVisualizer.ts): a lookup, not new
    // plumbing — no new hub method, no new wire field. voice.start() above just built this
    // peer's <audio> element synchronously, so it is always available here when the codec was
    // supported; undefined (voice.getAudioElement returning nothing) covers the unsupported-codec
    // case, where there is no playback graph to tap in the first place.
    const audio = voice.getAudioElement(peerId);
    if (audio) {
      avatars.voiceStreamStarted(peerId, audio);
    }
  });
  hub.on('voiceChunk', (update) => {
    if (update.peerId) {
      voice.appendChunk(update.peerId, update.data);
    }
  });
  hub.on('voiceStreamEnded', (peerId) => {
    voice.end(peerId);
    avatars.voiceStreamEnded(peerId);
  });

  // Same rule for a stroke still being drawn: never written to state, rendered directly by
  // the view instead. No colour rides on the message — a stroke being watched mid-draw
  // renders in its drawer's own presence colour, taking on its real ink colour only once
  // the commit lands (plan/whiteboard.md, "What it costs").
  hub.on('strokePoint', (update) => {
    const colorHex = (update.peerId && state.peers.get(update.peerId)?.colorHex) || '#FFFFFF';
    view.applyStrokePoint(update, colorHex);
  });

  // spec: SPEC-189
  /**
   * The lobby stands in the room exactly when the room is showing nothing — and, since
   * SceneRoot's lobby/working-room shells (SPEC-161, SPEC-189, SPEC-198), the shell around it
   * follows the identical pointer: opening or closing a diagram now changes the room itself, not
   * only the lobby's own miniatures on top of it.
   *
   * Driven off the snapshot's own pointer rather than off a flag this file keeps, so a room that
   * arrives showing nothing, a diagram closed, and a diagram opened by somebody else all reach
   * the same line — three paths that would otherwise each need to remember to do this.
   *
   * `shellDiagramId` (declared above, next to the initial `buildRoom` call) is what keeps the
   * rebuild conditional rather than unconditional: `sceneRoot.buildRoom` disposes and reconstructs
   * every mesh and material in the room (`updateRoomAccent`'s own doc comment explains why that is
   * expensive enough to avoid on every call, not merely wasteful), and every real caller of this
   * function already has a fresh room/template pair in hand whether or not the pointer's
   * null-ness actually changed — a `diagramShown` for a room already showing a diagram, most
   * plausibly. Comparing against what the shell was last built from, rather than rebuilding
   * unconditionally, is what makes this "rebuild when the diagram pointer changes" rather than
   * "rebuild every time this function runs."
   */
  const showLobbyIfIdle = (room: RoomDto, template: RoomTemplateDto) => {
    const diagramId = room.diagramId ?? null;
    const idle = diagramId === null;
    lobby.setEnabled(idle);
    lobbyInput.setActive(idle);

    // The mirror image (SPEC-199/SPEC-200): a sign to *leave* the lobby (return.setActive(false))
    // only makes sense while there is a diagram open to close. Closed over here rather than
    // referenced before its own declaration further down — `lobbyReturnSign` is constructed in
    // the "authoring" section below, well before this function is ever actually called (its first
    // call is after `hub.join()`, further down still) — the same "closed over, not yet declared"
    // shape `hub.onResynced`'s own `debugPanel`/`exitSign` references already use in this file.
    lobbyReturnSign.setActive(!idle);

    if (diagramId !== shellDiagramId) {
      sceneRoot.buildRoom(room, template);
      shellDiagramId = diagramId;
    }

    if (idle) {
      void getLobbyDiagrams()
        .then((rows) => lobby.setDiagrams(rows))
        .catch(() => {
          // An unreachable list leaves an empty lobby rather than a broken session. Nothing else
          // in the room depends on it, and the person can still leave.
        });
    }
  };

  /**
   * Somebody in this room opened a different diagram (SPEC-064). Nobody joined and nobody left,
   * so this deliberately does NOT go through `state.reset` — see `applyDiagramShown`'s own
   * remarks: resetting would clear the peer map and this client's own identity, making every
   * avatar in the room blink out and back for a change that moved nobody.
   */
  hub.on('diagramShown', (shown) => {
    state.applyDiagramShown(shown.snapshot, shown.activeLocks);
    view.setRoomFit(shown.snapshot.template.floorplan);
    showLobbyIfIdle(shown.snapshot.room, shown.snapshot.template);
  });

  hub.onResynced = (result) => {
    // A reconnect may land in a room that changed while we were away, template included — always
    // rebuilds, unconditionally, unlike `showLobbyIfIdle`'s own conditional rebuild: a resync's
    // *diagramId* can be identical to what the shell was already built from while the accent,
    // name or floorplan dimensions still changed underneath it, which `shellDiagramId` alone
    // could never detect.
    sceneRoot.buildRoom(result.snapshot.room, result.snapshot.template);

    // Keeps `shellDiagramId` truthful about what the shell was actually just built from, so a
    // `showLobbyIfIdle` call reached afterward (`diagramShown`, `openDiagram`) compares against
    // reality rather than whatever this session's pointer was before the reconnect.
    shellDiagramId = result.snapshot.room.diagramId ?? null;

    // Told again, before `state.reset` below rebuilds every mesh from it — a resync's template
    // could genuinely differ from the one this session started with, same reasoning as every
    // other floorplan-derived setup in this callback.
    view.setRoomFit(result.snapshot.template.floorplan);

    state.reset(result.snapshot, result.peers, result.activeLocks, result.self);

    // The desktop boundary clamp needs the identical treatment (A3, above): a resync's
    // template could genuinely differ, so the clamp is told again rather than left holding
    // whatever extents the room happened to have when this session started.
    desktop.setRoomExtents(
      result.snapshot.template.floorplan.width,
      result.snapshot.template.floorplan.depth,
    );

    // The debug panel's own south-wall placement is derived from the floorplan
    // (DebugPanel.reposition), which a resync's template could genuinely differ on — a no-op
    // for anyone but `lars`, and closed over here rather than referenced before its own
    // declaration further down: this callback only ever runs later, after hub.start()
    // resolves, by which point debugPanel already exists.
    debugPanel.reposition(result.snapshot.template.floorplan);

    // The exit sign's own north-wall placement needs the identical treatment — visible to
    // everyone, unlike debugPanel, but positioned the same "re-derive from the floorplan on
    // every resync" way.
    exitSign.reposition(result.snapshot.template.floorplan);

    // The return-to-lobby sign needs the identical treatment — same wall, same "re-derive on
    // every resync" reasoning. This callback does not call `showLobbyIfIdle` at all (it never
    // did, even for `lobby.setEnabled`/`lobbyInput.setActive` — a pre-existing gap this change
    // does not widen or narrow), so `reposition()` alone is exactly as complete here as it is for
    // `exitSign` just above: it re-derives the wall mount from a resync's template, and leaves
    // whichever `active` state was already in effect untouched.
    lobbyReturnSign.reposition(result.snapshot.template.floorplan);

    // The error log's own south-wall placement needs the identical treatment — a resync's
    // template could differ. The room name painted onto the north beam already survived this
    // resync too: `buildRoom` above just rebuilt it from `result.snapshot.room.name`
    // (plan/in-room-signage.md, "It must survive a resync that could carry a different name").
    errorLog.reposition(result.snapshot.template.floorplan);

    avatars.clear();
    for (const peer of result.peers) {
      avatars.add(peer);
    }

    refreshCounts();
  };

  // A reconnect's rejoin was refused — the room is gone, or this caller's own org
  // membership was revoked mid-session (`plan/tenancy-resolution.md`), both of which
  // `JoinRoom` throws a clean `HubException` for. Without this, `roomHub.ts`'s own catch
  // around the rejoin (its `onResyncFailed`'s doc comment names this exact gap) had nowhere
  // to report to — `onConnectionStateChanged` had already told the overlay `Connected`, so
  // RoomState/RoomView would keep silently rendering a room this connection is no longer
  // actually in. showFatalError is the right response, not a soft notice: nothing about
  // this session can continue, and the fatal screen already offers sign-out plus a portal
  // link (`FatalErrorScreen.ts`), exactly the escape a revoked membership needs.
  hub.onResyncFailed = (error) => showFatalError(error);

  await hub.start();
  splash.setCheck('backend', 'connected to the backend', 'ready');

  // --- authoring --------------------------------------------------------------------

  const grabbers = new GrabberHub(sceneRoot.scene);

  // A permanent, `lars`-only in-room affordance (plan/open-items.md) — see DebugPanel.ts's own
  // doc comment for why it exists and why the gate is a convenience, not a security boundary.
  // `currentAccount` was already read once, at sign-in, above; this is not a second fetch.
  const debugPanel = new DebugPanel(sceneRoot.scene, grabbers, hub, state, isOwnersOwnAccount(currentAccount));
  debugPanel.onNotice = notify;
  // Not also a `roomUpdated` broadcast for this caller (RoomHub.updateRoomAccent's own doc
  // comment) — applying it here mirrors the re-tint `hub.on('roomUpdated', ...)` above already
  // drives for every other peer.
  debugPanel.onAccentChanged = (room) => sceneRoot.updateRoomAccent(room.accentColor);
  // The room's own dimensions are already known from the snapshot fetched above — positioning
  // does not need to wait for anything else constructed in this section.
  debugPanel.reposition(snapshot.template.floorplan);

  // The in-headset way to leave a session (plan/open-items.md — see ExitSign.ts's own module
  // comment for the full account). Unlike debugPanel, this is visible to everyone: every person
  // in the room needs a way out, not just `lars`. `onLeaveRequested` is wired further down,
  // once `xr` exists (`xr` is not constructed until the --- xr --- section below).
  const exitSign = new ExitSign(sceneRoot.scene, grabbers);
  exitSign.reposition(snapshot.template.floorplan);

  // The other half of SPEC-200 — closing the diagram and returning to the lobby, distinct from
  // ending the session above (see LobbyReturnSign.ts's own module comment for why it is a second
  // fixture rather than a second meaning on `exitSign`). Also visible to everyone, and
  // repositioned the same way. Whether it is currently the interactive surface at all —
  // `setActive` — is driven from `showLobbyIfIdle`, above, off the identical "is a diagram open"
  // fact `lobby`/`lobbyInput` already key off; it starts inactive here and is brought up to date
  // the first time `showLobbyIfIdle` actually runs (after `hub.join()`, further down).
  const lobbyReturnSign = new LobbyReturnSign(sceneRoot.scene, grabbers);
  lobbyReturnSign.reposition(snapshot.template.floorplan);

  // debugPanel's own local, client-only override of the assistant glow (DebugPanel.ts's own
  // module comment) — a plain passthrough to the same SceneRoot setters a real turn drives,
  // exactly like assistant.onGlowChanged/onAmplitudeChanged below.
  debugPanel.onDebugGlowChanged = (glow) => sceneRoot.setAssistantGlow(glow);
  debugPanel.onDebugAmplitude = (level) => sceneRoot.setAssistantAmplitude(level);

  // The grid-snapping visibility toggle (plan/grid.md) — a plain passthrough, same shape as
  // the glow overrides just above. See DebugPanel.ts's own `gridVisible` doc comment for why
  // this exists as a toggle rather than a decision made in code.
  debugPanel.onGridVisibleChanged = (visible) => sceneRoot.setCellGridVisible(visible);

  // A real assistant turn always wins over debugPanel's override: SceneRoot.setAssistantGlow
  // does not know which caller it came from, so applying the real state here already overwrites
  // whatever the debug panel last set, for free. What that does not do on its own is stop the
  // debug panel's synthetic 'speaking' amplitude timer, which would otherwise keep firing and
  // periodically clobbering the real, audio-driven amplitude with a fake one — debugPanel.
  // yieldToRealGlow() stops that timer and resets the panel's own cycle position, so the next
  // debug press starts fresh rather than resuming a state nobody chose. See DebugPanel.ts's own
  // module comment for the full reasoning.
  assistant.onGlowChanged = (glow) => {
    sceneRoot.setAssistantGlow(glow);
    debugPanel.yieldToRealGlow();
  };

  // The single off-hand status glyph (plan/mic-indicator.md) — mic state pushed from
  // AssistantController.onIndicatorChanged below, the room's assistant glow read live off
  // sceneRoot every tick (see MicIndicatorRenderer's own module doc for why that half is a poll
  // rather than a second callback). Not started here — see the per-frame registrations further
  // down, alongside grabbers/authoring/devSim/avatars.
  const micIndicator = new MicIndicatorRenderer(sceneRoot.scene, grabbers, sceneRoot);
  assistant.onIndicatorChanged = (state) => micIndicator.setState(state);

  const authoring = new AuthoringController(
    sceneRoot.scene,
    grabbers,
    state,
    view,
    hub,
    streamer,
    { orgId: snapshot.room.orgId, roomId, diagramId: snapshot.room.diagramId ?? null },
    sceneRoot.whiteboard,
  );

  // The authored prop models, preloaded the same "before first use, not on demand" way
  // avatars.preloadModels()/view.preloadModels() already are above — ToolPropRenderer's own
  // ensure* methods and TrashCanRenderer.ensureMesh all build synchronously, on the first tick
  // each is shown, so this has to settle before then rather than on first use
  // (plan/open-items.md, "The whiteboard marker model is authored and unused"). Whatever has not
  // resolved by here falls back to each class's own procedural shape for the rest of the
  // session. Independent preloads, not raced together with Promise.all: neither depends on the
  // other's outcome, and a shared failure would be no easier to diagnose than two separate
  // console lines already are.
  await authoring.toolProp.preloadModels();
  await authoring.trashCan.preloadModels();

  // The tray offers exactly what the server will accept, because both read the same catalogue.
  authoring.palette.setCatalog(nodeTypes);
  authoring.palette.setCapacity(roomCapacity);

  // Live, not a value captured when the tray opened — a node created or deleted by anyone
  // while the tray is showing has to move the readout, the same "counts what a room actually
  // contains at the moment" rule the server's own enforcement follows
  // (RoomMutationService.CreateNodeAsync).
  authoring.palette.nodeCount = () => state.nodes.size;

  authoring.onNotice = notify;

  const devSim = new DevSim(sceneRoot.scene, canvas, grabbers, authoring, assistant);

  Object.assign(devHandle, {
    grabbers,
    authoring,
    devSim,
    micIndicator,
    errorLog,
    debugPanel,
    exitSign,
    lobbyReturnSign,
  });

  // --- xr ---------------------------------------------------------------------------

  // Known once the room is joined; the session screen needs it when a session starts.
  let sessionInfo = { room: snapshot.room.name, displayName: currentAccount.displayName };

  // What the browser window shows is driven by this, not by XR session state. Set from the
  // splash choice below, and again by anything that explicitly asks for a different mode
  // afterwards — the reenter button, the session screen's own Enter VR / Desktop mode
  // buttons. Ending a session never writes to it: leaving VR is not a request to see the
  // room, it is just the session ending. See the note on onExit just below.
  let currentMode: EntryMode = 'desktop';

  // "Requested, outcome not known yet" — true the first time this runs, and true again on
  // every re-entry after a session has ended: the previous grant or refusal is a fact about
  // that session, not a standing fact about this browser, so a fresh attempt is a genuinely
  // new "not yet known" moment, not a repeat of the same answer.
  const resetCapabilitiesPending = () => {
    sessionScreen.setCapability(
      'bounded-floor',
      'play-area bounds — requested, awaiting session',
      'pending',
    );
  };

  const xr = await XrManager.createAsync(sceneRoot.scene, sceneRoot.floorMeshes, {
    // Snap-turn and teleport read the exact stick the off-hand tray navigates by, so both must
    // stop for as long as it is genuinely being navigated — narrowed to `surfaceKind ===
    // 'palette'` (plan/mode-hierarchy.md: "still needed, but now only in Build/Place... In
    // Draw, Scan and Link the off-hand stick is never repurposed — it keeps doing locomotion
    // throughout, because there is nothing on that hand's surface for it to navigate"). A plain
    // passthrough rather than anything computed here: XrManager re-polls this every frame, so
    // whatever makes the tray stop being open, or stop being the interactive surface — the grip
    // released, a mode switch into Draw/Scan/Link, the controller gone, the session ending — is
    // reflected within one frame with nothing further to wire up.
    travelSuppressed: () => authoring.palette.isOpen && authoring.palette.surfaceKind === 'palette',

    onEnterRequested: () => {
      // Cover the canvas here, not in onEnter: this fires the instant entry begins, while
      // onEnter only fires once the session is fully established. Between the two the canvas
      // is still rendering the ordinary desktop view — the one person who could look at it is
      // wearing the headset either way, so there is no moment worth leaving it on screen for.
      // It is also where the frame rate now lives, since nothing is welded to the face inside
      // the session any more.
      resetCapabilitiesPending();
      sessionScreen.show(sessionInfo);
    },
    onEnter: (experience) => {
      overlay.set({ mode: 'immersive-vr' });

      grabbers.attachXr(experience);

      if (hint) {
        hint.hidden = true;
      }

      if (reenterButton) {
        reenterButton.hidden = true;
      }

      if (leaveRoomButton) {
        leaveRoomButton.hidden = true;
      }
    },
    onExit: () => {
      overlay.set({ mode: 'desktop' });

      // Ending a VR session takes the cover down and reveals the splash — the room selector —
      // rather than swapping Exit VR for a second, dead-end pair of buttons
      // (plan/session-exit-and-membership-fixes.md, A1; SessionScreen.ts's own doc comment has
      // the full reasoning, including the owner's own correction: "I don't want an extra
      // button. I want to go back to the room selector page at /room"). Guarded on
      // `currentMode === 'vr'` for the same reason it always was: this callback exists on the
      // `xr` object regardless of which mode is chosen, and only a session actually entered
      // through this app's own code (which always sets `currentMode = 'vr'` first) should ever
      // reveal anything here.
      if (currentMode === 'vr') {
        sessionScreen.hide();
        splash.reopen();
      }
    },
    onSetupIncomplete: (missing) => {
      // Surfaced rather than swallowed: Babylon returns a half-built experience on error,
      // and a session that starts but cannot move is worse than one that says so. Also how a
      // controller-less session is reported — controllers are required for every in-headset
      // action now that hand tracking is gone (2026-08-10) — XrManager reports that here as
      // `'controllers'` once it has had a moment to see whether any connected.
      splash.setCheck('xr-degraded', `XR set up without: ${missing.join(', ')}`, 'unavailable');

      if (missing.includes('controllers')) {
        sessionScreen.setCapability(
          'controllers',
          'no controllers connected — nothing in this session can be acted on',
          'broken',
        );
      }
    },
    onBoundedFloorChanged: (granted) => {
      sessionScreen.setCapability(
        'bounded-floor',
        granted ? 'play-area bounds — granted' : 'play-area bounds — requested, not granted',
        granted ? 'ready' : 'unavailable',
      );
    },
  });

  Object.assign(devHandle, { xr });

  // A rendered pen, eraser or scanner now extends from the aim pose toward the board whenever
  // the dominant hand is in draw or scan mode (plan/history/control-modes.md, M12B; scanner added
  // alongside the marker sizes), which puts it roughly where the controller model already
  // renders — hide that model for as long as a prop is shown, so the two stop visibly
  // overlapping, the same reasoning M11 established for the (since deleted) PenLedge.
  // ToolPropRenderer decides *when* to hide or show and *which* hand,
  // including across a live handedness flip mid-hold; this is a plain forward into XrManager,
  // the same arm's-length shape `travelSuppressed` already uses.
  authoring.toolProp.onSetControllerModelVisible = (visible) =>
    xr?.setDominantControllerModelVisible(visible);

  // The trash can does the same for the off hand while it is shown — reported twice from a
  // headset before this existed (TrashCanRenderer.ts's own doc on `onSetControllerModelVisible`
  // has the quotes): a bin overlaying the controller model reads as a floating decoration, not
  // as something that has replaced the hand.
  authoring.trashCan.onSetControllerModelVisible = (visible) =>
    xr?.setOffControllerModelVisible(visible);

  // Requested every session, so the row exists from here on — 'pending' until
  // onBoundedFloorChanged above reports "what a session actually grants," a different, later
  // answer than "what the browser claims to support" — see plan/play-area.md, "Removed after a
  // headset test", and CLAUDE.md on WebXRDefaultExperience swallowing its own setup errors.
  resetCapabilitiesPending();

  splash.setVrAvailable(xr !== null);
  splash.setCheck(
    'headset',
    xr === null ? 'no headset — desktop only' : 'headset ready',
    xr === null ? 'unavailable' : 'ready',
  );

  // A missing feature import is a silent failure at session start, so surface what the
  // bundle registered while the console is still being read.
  console.info('WebXR features registered:', XrManager.availableFeatures().join(', '));

  splash.setRoomReady(true);

  /**
   * The only place the room becomes visible in this browser window. Sets currentMode to
   * 'desktop' and takes the cover down (if it was ever up) — called for an explicit Desktop
   * choice on the splash (both the very first one, and any later one — see `enterMode` below),
   * and as the fallback when a VR entry attempt fails, so a failed attempt does not strand the
   * tab covered with nothing behind it to fall back to.
   */
  const settleIntoDesktop = () => {
    currentMode = 'desktop';
    sessionScreen.hide();
    overlay.set({ mode: 'desktop' });

    if (hint) {
      hint.hidden = false;
    }

    if (reenterButton && xr !== null) {
      reenterButton.hidden = false;
    }

    // Desktop mode's own way out (plan/session-exit-and-membership-fixes.md, A2) — shown
    // whenever the room is actually visible, unconditionally: unlike `reenterButton`, this
    // does not need a headset to be worth having, since it leads back to the splash's room
    // selector, not into VR.
    if (leaveRoomButton) {
      leaveRoomButton.hidden = false;
    }
  };

  /**
   * Acts on a mode choice — the very first one, once `choicePromise` resolves just below, and
   * every later one `splash.onModeChosen` (wired further down, once this session has actually
   * joined) reports after a VR session ends or Desktop mode's own leave control fires
   * (plan/session-exit-and-membership-fixes.md, A1/A2). One function for both, so a failed VR
   * attempt's fallback to Desktop mode behaves identically regardless of which of the two asked
   * for VR in the first place.
   */
  const enterMode = async (mode: EntryMode): Promise<void> => {
    currentMode = mode;
    splash.hide();

    if (mode === 'vr' && xr !== null) {
      try {
        await xr.enterAsync();
      } catch (error) {
        console.warn('Entering VR failed.', error);
        splash.showError(
          `Could not start the session: ${error instanceof Error ? error.message : String(error)}`,
        );
        splash.reopen();
        settleIntoDesktop();
      }
    } else {
      settleIntoDesktop();
    }
  };

  // --- entry ------------------------------------------------------------------------

  const choice = await choicePromise;
  currentMode = choice.mode;

  // The spectator role (plan/roadmap.md, Phase 3): built as "this client asserts a role at
  // join," not as "desktop means spectator" — the role is general, and a headset user could
  // deliberately spectate later. What decides it *today* is `choice.mode`, the same signal
  // that already decides whether an XR session is ever entered at all — deliberately not
  // `XrManager.isSupported()`: that answers device capability, and
  // plan/identity-and-portal.md's own "capability is not intent" note applies here unchanged
  // — a desktop machine with a headset plugged in reports XR-capable and still lands here
  // through the desktop choice. Desktop simply always asserts it for now.
  const isSpectator = choice.mode === 'desktop';

  // No name passed: the server reads it off this connection's authenticated principal.
  // SignalR binds `JoinRoom`'s arguments strictly by count (IRoomHubServer.JoinRoom's own
  // remarks) — `isSpectator` has to be sent explicitly every time, never left to a default.
  const join = await hub.join(roomId, isSpectator);
  state.reset(join.snapshot, join.peers, join.activeLocks, join.self);

  // A room showing nothing is where a session begins (SPEC-161), so this is the ordinary arrival
  // rather than an edge case: the lobby stands in the room and your diagrams are what you see.
  // `shellDiagramId` already matches this snapshot's own pointer (set right alongside the
  // earlier, initial `sceneRoot.buildRoom` call above), so this ordinarily only sets the lobby's
  // own enabled/active flags — it still rebuilds the shell if `join`'s own snapshot somehow
  // disagrees with the REST snapshot the room was first built from (a live edit landing in the
  // gap between the two fetches).
  showLobbyIfIdle(join.snapshot.room, join.snapshot.template);

  for (const peer of join.peers) {
    avatars.add(peer);
  }

  // Both halves are known only now: whether this browser ever granted microphone
  // permission (the splash's own gesture, above, may still be settling — read as of right
  // now regardless) and whether this server has an assistant configured at all
  // (RoomSnapshotDto.assistantAvailable, resolved once at server startup). The assistant is
  // unreachable for a spectator (the server already refuses BeginAssistantTurn for one —
  // RoomSyncHub.BeginAssistantTurn); `isSpectator` here is what makes beginTalk stay inert on
  // the client too, rather than asking and being refused.
  assistant.configure(micPermissionGranted, join.snapshot.assistantAvailable, isSpectator);

  sessionInfo = { room: join.snapshot.room.name, displayName: join.self.displayName };

  if (xr !== null) {
    sessionScreen.onExitRequested = () => void xr.exitAsync();

    // ExitSign.ts's own aim-and-hold plate, wired to the exact same call the browser's own
    // exit button above makes — see that file's own module comment for why leaving from
    // inside a session and leaving from the browser both have to go through one teardown path.
    exitSign.onLeaveRequested = () => {
      // The sign can only realistically be aimed at from inside a live session, but this
      // mirrors the guard this route's own cleanup uses for the identical call further down
      // (`if (xr?.inXr) { void xr.exitAsync(); }`) — harmless if a hold somehow completes after
      // the session already ended some other way (a server-side disconnect, a dropped
      // connection), and cheap insurance against calling exitXRAsync twice.
      if (!xr.inXr) {
        return;
      }

      // Release whatever this client is holding before ending the session. exitAsync() only
      // ends the *XR* session — it does not stop the hub connection (the same "leaving VR is
      // just the session ending, not a request to leave the room" distinction onExit's own
      // comment below draws) — so RoomSyncHub.OnDisconnectedAsync's own held-node cleanup does
      // not run here. Without this, a node grabbed the instant before leaving would stay
      // locked to a peer no longer even looking at the room until the hub's own connection
      // timeout eventually caught it. The same release `releaseOnLeave` (further down) performs
      // for a real disconnect.
      authoring.possession.releaseImmediately();

      void xr.exitAsync();
    };
  }

  // Never assigned at all for a spectator (SPEC-200's own module comment on LobbyReturnSign.ts):
  // a spectator has no write access to change what the room shows
  // (RoomSyncHub.ShowDiagram's own refusal), so leaving this null is what makes a completed hold
  // do nothing for them, the same "just does nothing" shape DevSim.spectator already gives every
  // other authoring-only action a spectator might stumble into.
  if (!isSpectator) {
    lobbyReturnSign.onReturnRequested = () => {
      // The same defensive release ExitSign's own wiring performs, and for the identical reason:
      // ShowDiagram's own server-side lock release happens as part of the mutation, but this
      // client's own PossessionManager state (heldNodeId, the held-look material override) would
      // otherwise still reference a mesh the new, diagram-less snapshot never rebuilds.
      authoring.possession.releaseImmediately();
      void closeDiagram();
    };
  }

  // Every mode choice made from here on — the splash reappearing after a VR session ends (A1),
  // or Desktop mode's own leave control (A2, wired just below) — funnels through `enterMode`,
  // the same place the very first choice (below, once `choicePromise` resolves) goes through.
  // Assigned only now, after the initial choice has already been made through `waitForChoice()`
  // — see `onModeChosen`'s own doc on `Splash` for why that ordering is what keeps the first
  // press from double-firing.
  splash.onModeChosen = (mode) => void enterMode(mode);

  if (leaveRoomButton) {
    leaveRoomButton.addEventListener('click', () => {
      leaveRoomButton.hidden = true;
      splash.reopen();
    });
  }

  overlay.set({
    room: `${join.snapshot.room.name} · ${join.self.displayName}`,
    mode: choice.mode === 'vr' ? 'entering vr…' : 'desktop',
  });
  refreshCounts();

  // Registration order is the frame order: grabbers refresh, then gestures read them, then
  // avatars advance, then whatever changed is streamed.
  const stopGrabbers = grabbers.start();

  // Authoring is unreachable for a spectator — gated here, not deleted (CLAUDE.md, "the
  // pointer authoring code stays, gated out of the product"): roughly a dozen automated tests
  // drive placement, grabbing, connections and board contact straight through
  // `AuthoringController`/`PlacementManager`/`PossessionManager`/`ConnectionDrawer`/
  // `DrawingManager` under NullEngine, and every one of them constructs its own instance
  // directly rather than through this file, so none of them are affected by skipping this
  // call. Not calling `authoring.start()` at all means none of its per-frame gesture
  // processing (placement, possession, connections, drawing, scan, the tray's own tick) ever
  // runs for this session; `devSim.spectator` (set below) is what stops its hotkeys from
  // reaching into `authoring`'s state directly, since those calls do not go through
  // `authoring.start()` at all — see `DevSim.spectator`'s own doc for exactly which ones.
  if (!isSpectator) {
    // Handed over before start(), so the very first arbitrated frame already knows whether the
    // lobby is the interactive surface. A spectator gets neither: they may look at the lobby but
    // not steer the room out of it, the same rule ShowDiagram enforces server-side.
    authoring.lobbyInput = lobbyInput;

    authoring.start(sceneRoot.scene);
  }

  devSim.spectator = isSpectator;
  const stopDevSim = devSim.start();
  const stopAvatars = avatars.start();
  const stopMicIndicator = micIndicator.start();
  const stopDebugPanel = debugPanel.start();
  const stopExitSign = exitSign.start();
  const stopLobbyReturnSign = lobbyReturnSign.start();

  // What this connection last told the room its own mode/hand was — plan/remote-tool-
  // visibility.md. `null` until the very first tick below runs, which is deliberate: it makes
  // that first tick unconditionally send the current (Build, dominant hand) state, giving every
  // participant a retained server-side entry from the moment they join rather than only once
  // they first leave Build — see ToolSwitchRegistry.cs's own remarks for why that is the more
  // literal reading of "one tool-switch event per peer already in the room," not merely a safe
  // default.
  let lastSentTool: { mode: string; hand: string } | null = null;

  const streamObserver = sceneRoot.scene.onBeforeRenderObservable.add(() => {
    // X/Y have no arbitration dependency on grab/place/draw/connect — a peer, not a
    // possession gesture — so this reads no more than "grabbers already refreshed this
    // frame," true by registration order alone (grabbers.start() above).
    assistant.tick(grabbers.byRole('off'));

    // The dominant hand's own copy of the same physical button, read for a different
    // gesture entirely (plan/open-items.md) — see NodeLabelController.tick's own doc
    // comment. Reads possession.heldNodeId fresh every tick, which is fine: this is only
    // ever consulted at the instant the button is first pressed (NodeLabelController.begin),
    // never afterward.
    nodeLabel.tick(grabbers.byRole('dominant'), authoring.possession.heldNodeId);

    // Announces a mode or handedness change to the room, edge-triggered off the last value this
    // connection itself sent (plan/remote-tool-visibility.md) — never for a spectator, who has
    // no controllers and therefore no mode to announce (this file's own deliberate choice for
    // "what does a spectator read as": nothing, ever, not an uninitialised value). Read fresh
    // every tick, the same "no cached value that can go stale" rule every other per-tick read in
    // this file already follows — cheap: `ToolMode.mode`/`handedness.dominant` are both plain
    // field reads, not derived work.
    if (!isSpectator) {
      const mode = authoring.toolMode.mode;
      const hand = handedness.dominant;

      if (lastSentTool === null || lastSentTool.mode !== mode || lastSentTool.hand !== hand) {
        lastSentTool = { mode, hand };
        hub.switchTool(mode, hand);
      }
    }

    // Last in the frame, after the gestures have moved anything they are moving: a name plate
    // has to follow the node it names, and it turns to face the camera every frame regardless.
    view.updateLabels();

    // Advances any node or stroke currently fading out after a delete — see RoomView.queueFade.
    view.tickFades();

    // Advances the scan tool's own pulse/fade-out glow (RoomView.tickScanGlow) — on
    // onBeforeRenderObservable rather than requestAnimationFrame for the same reason every
    // other continuous animation in this file is: CLAUDE.md, "requestAnimationFrame is
    // throttled inside an active WebXR session." The engine's own per-frame delta, not a
    // second clock of this file's own.
    view.tickScanGlow(sceneRoot.scene.getEngine().getDeltaTime() / 1000);

    sampleOwnPose();
    streamer.flush(Date.now());
  });

  // Kept rather than discarded so per-frame work can be torn down — and so an automated
  // check can stop the desktop pointer while injecting input of its own.
  Object.assign(devHandle, {
    stopGrabbers,
    stopDevSim,
    stopAvatars,
    stopMicIndicator,
    stopDebugPanel,
    stopExitSign,
    stopLobbyReturnSign,
    stopStreaming: () => sceneRoot.scene.onBeforeRenderObservable.remove(streamObserver),
  });

  function sampleOwnPose(): void {
    const camera = sceneRoot.scene.activeCamera;

    if (!camera) {
      return;
    }

    const rotation = camera.absoluteRotation;
    const head: PoseDto = {
      p: vec3ToArray(camera.globalPosition),
      r: quatToArray(rotation),
    };

    const pack = (handedness: XRHandedness): PoseDto | null => {
      const grabber = grabbers.byHandedness(handedness);

      if (grabber === undefined || !grabber.tracked) {
        return null;
      }

      return {
        p: vec3ToArray(grabber.position),
        r: quatToArray(grabber.rotation),
      };
    };

    // A desktop peer has one "hand", the cursor. Reported as the right hand so remote
    // viewers see someone pointing rather than a floating head.
    const pointer = pack('none');

    // Either signal counts as "that hand is doing something", which is all a remote avatar
    // needs — it shows a closed fist, not which button produced it.
    const busy = (handedness: XRHandedness): boolean => {
      const grabber = grabbers.byHandedness(handedness);

      return (grabber?.near.pressed ?? false) || (grabber?.far.pressed ?? false);
    };

    streamer.sampleAvatar(
      head,
      pack('left'),
      pack('right') ?? pointer,
      busy('left'),
      busy('right') || busy('none'),
    );
  }

  // Exhaustive on purpose: "VR was chosen but there is no session" must land somewhere
  // definite, or the overlay sits on "entering vr…" forever with no session coming. Also
  // handles the "headset asleep, permission dismissed, another session already running"
  // failure case — the splash is the only way to choose again, so it has to be reachable:
  // `settleIntoDesktop` takes the cover down as part of falling back, which matters here
  // specifically — the cover sits above everything except the splash, and the splash does not
  // raise itself above a cover that is still up, so without this the reopened splash would be
  // stuck behind it. `enterMode` is the shared place all of this lives, since a later VR
  // attempt from the splash's own reentry (A1) needs the identical fallback.
  await enterMode(choice.mode);

  if (reenterButton && xr !== null) {
    reenterButton.addEventListener('click', () => {
      reenterButton.hidden = true;
      currentMode = 'vr';

      void xr.enterAsync().catch((error: unknown) => {
        console.warn('Entering VR failed.', error);

        // The attempt never got in, so it should not stick as the chosen mode either —
        // fall back the same way the very first attempt does, which also restores
        // reenterButton's visibility.
        settleIntoDesktop();
      });
    });
  }

  // Twice a second is enough for a readout nobody is staring at, and keeps it off the
  // render path.
  const statsInterval = window.setInterval(() => {
    sessionScreen.update({
      peers: [...state.peers.values()].map((peer) => peer.displayName),
      fps: sceneRoot.engine.getFps(),
      nodes: state.nodes.size,
      connections: state.connections.size,
    });
  }, 500);

  /**
   * Drop anything held, so a refresh does not leave a node locked until the server times
   * the connection out. Same reasoning for the assistant channel: a refresh mid-turn
   * should not leave the room's ceiling stuck blue until OnDisconnectedAsync's own
   * cleanup eventually catches it server-side. Named rather than inline so the router's own
   * cleanup below (an in-app navigation away from `/room`, not a page unload) can reuse the
   * exact same release logic and then also remove this listener — a page unload is not the
   * only way this room stops being live now.
   */
  const releaseOnLeave = () => {
    authoring.possession.releaseImmediately();
    assistant.releaseImmediately();
    nodeLabel.releaseImmediately();
    voice.releaseAll();
    void hub.stop();
  };

  window.addEventListener('beforeunload', releaseOnLeave);

  // Returned to the router as this route's own cleanup, run once before the next route
  // renders (Router.ts). Not exercised by an automated headset session — the owner's stated
  // "proper cleanup later" for the VR entry screen covers the in-session UI, not this; this
  // is the best-effort teardown for the in-app "leave /room without a page reload" path the
  // router now makes possible, reusing every stop handle this function already built rather
  // than inventing a second set. If a live XR session is still running, it is asked to exit
  // (fire-and-forget — nothing here can usefully wait on it) before the engine underneath it
  // is disposed out from under it.
  return () => {
    window.removeEventListener('beforeunload', releaseOnLeave);
    window.removeEventListener('error', handleWindowError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    window.clearInterval(statsInterval);

    stopGrabbers();
    stopDevSim();
    stopAvatars();
    stopMicIndicator();
    stopDebugPanel();
    stopExitSign();
    stopLobbyReturnSign();
    sceneRoot.scene.onBeforeRenderObservable.remove(streamObserver);

    releaseOnLeave();

    if (xr?.inXr) {
      void xr.exitAsync();
    }

    // The disposes `sceneRoot.dispose()` alone does not cover — CLAUDE.md's own "That same
    // cascade does not free materials" note is about `TransformNode.dispose()`, and
    // `scene.dispose()` (which `sceneRoot.dispose()` calls) is not that call at all: it walks
    // every mesh, material and observable the scene itself tracks, which is why this was easy
    // to believe already covered everything. Two things it genuinely does not reach: each
    // peer's own `AudioContext` (`avatars.dispose()` -> `Avatar.dispose()` -> `voiceBands.stop()`
    // -> `audioContext.close()`, none of it scene-owned) and `DebugOverlay`'s DOM node, which
    // lives in `#status`, outside the router's own `#portal` mount and therefore untouched by
    // whatever the next route renders. All five run before `sceneRoot.dispose()` below, since
    // `view`/`avatars`/`overlay`/`desktop`/`xr` all still need the live scene they were built
    // against to tear themselves down cleanly.
    view.dispose();
    lobby.dispose();
    avatars.dispose();
    overlay.dispose();
    desktop.dispose();
    xr?.dispose();

    splash.dispose();
    sessionScreen.dispose();
    sceneRoot.dispose();
  };
}
